/* eslint-disable vue/multi-word-component-names */
import { createApp, type Plugin } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

import './assets/scss/styles.scss'
import './assets/scss/main.scss'

import App from './App.vue'
import router from './router'
import { useAuthStore } from './stores/auth'

import PhilaUICore from '@phila/phila-ui-core'
import Dropdown from '@phila/phila-ui-dropdown'
import Textbox from '@phila/phila-ui-textbox'
import AppHeader from '@phila/phila-ui-app-header'
import AppFooter from '@phila/phila-ui-app-footer'
import TabsNav from '@phila/phila-ui-tabs-nav'
import MobileNav from '@phila/phila-ui-mobile-nav'
import NavLink from '@phila/phila-ui-nav-link'
import DropdownNav from '@phila/phila-ui-dropdown-nav'
import Modal from '@phila/phila-ui-modal'
import { Form as VeeForm, Field, defineRule } from 'vee-validate'
import VeeDropdown from './components/vee-dropdown.vue'
import VeeTextbox from './components/vee-textbox.vue'
import VeeTextarea from './components/vee-textarea.vue'
import VeeCheckbox from './components/vee-checkbox.vue'
import LoadingSpinner from './components/loading-spinner.vue'

import axios, { axiosGraph, defaultResponseController } from './plugins/axios'
import { notify, success, warning, error, fatalError } from './plugins/notifications'
import sCache from './plugins/simple-cache'
import vMask from './directives/mask'
import { parseClock } from './utils/time'

// ─────────────────────────────────────────────
// VeeValidate rules. Registered globally and consumed by the vee-* wrappers.
// ─────────────────────────────────────────────
defineRule('required', (value: unknown) => {
  if (value === undefined || value === null || value === '' || value === false) {
    return 'This field is required'
  }
  if (Array.isArray(value) && value.length === 0) {
    return 'This field is required'
  }
  return true
})

/** A 24-hour clock value, `HH:MM`, as typed through the `##:##` mask. */
defineRule('time', (value: unknown) => {
  if (value === undefined || value === null || value === '') return true
  return parseClock(String(value)) !== null
    ? true
    : 'Enter a time on the 24-hour clock, for example 13:30'
})

/**
 * Time End must be later than Time Start. Cross-field: the rule string names
 * the other field as `mintime:@timeStart`, and vee-validate passes its value.
 */
defineRule('mintime', (value: unknown, [target]: unknown[]) => {
  const end = parseClock(String(value ?? ''))
  const start = parseClock(String(target ?? ''))
  if (end === null || start === null) return true
  return end > start ? true : 'The Time End field must be greater than Time Start field.'
})

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

const app = createApp(App)
app.use(pinia)

// phila-ui component registration, as the template does it.
app.use(PhilaUICore as Plugin)
app.component('AppHeader', AppHeader)
app.component('AppFooter', AppFooter)
app.component('TabsNav', TabsNav)
app.component('MobileNav', MobileNav)
app.component('NavLink', NavLink)
app.component('DropdownNav', DropdownNav)
app.component('Modal', Modal)
app.component('Dropdown', Dropdown)
app.component('Textbox', Textbox)
app.component('VeeForm', VeeForm)
app.component('Field', Field)
app.component('VeeDropdown', VeeDropdown)
app.component('VeeTextbox', VeeTextbox)
app.component('VeeTextarea', VeeTextarea)
app.component('VeeCheckbox', VeeCheckbox)
app.component('LoadingSpinner', LoadingSpinner)

app.directive('mask', vMask)

app.use(router)

// Vue 3 has no Vue.prototype; globalProperties is the equivalent.
app.config.globalProperties.$axios = axios
app.config.globalProperties.$axiosGraph = axiosGraph
app.config.globalProperties.$defaultResponseController = defaultResponseController
app.config.globalProperties.$notify = notify
app.config.globalProperties.$success = success
app.config.globalProperties.$warning = warning
app.config.globalProperties.$error = error
app.config.globalProperties.$fatalError = fatalError
app.config.globalProperties.$sCache = sCache

// Sign-in is by redirect, so a page load can be the return leg from Microsoft
// with the result still unread. Settling it before mounting is what makes the
// account exist by the time the router's first guard asks.
//
// Failure is not fatal. The app still mounts, unauthenticated, and the login
// page reports what went wrong from authStore.authError.
useAuthStore(pinia)
  .init()
  .catch((err) => console.error('MSAL failed to initialise: ', err))
  .finally(() => app.mount('#app'))
